import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_INTERVAL_MS,
  DEFAULT_SCROLL_DURATION_MS,
  DEFAULT_VIEWPORT_HEIGHT,
} from "./lib";

type Options = {
  itemCount: number;
  intervalMs?: number;
  scrollDurationMs?: number;
  viewportHeight?: number;
  enabled?: boolean;
};

export function useIntervalScrollVertical({
  itemCount,
  intervalMs = DEFAULT_INTERVAL_MS,
  scrollDurationMs = DEFAULT_SCROLL_DURATION_MS,
  viewportHeight = DEFAULT_VIEWPORT_HEIGHT,
  enabled = true,
}: Options) {
  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionToEnd, setTransitionToEnd] = useState(false);
  const transitionEndFired = useRef(false);

  const nextIndex = (index + 1) % itemCount;

  const handleTransitionEnd = useCallback(() => {
    if (!isTransitioning || transitionEndFired.current) return;
    transitionEndFired.current = true;
    setIndex((i) => (i + 1) % itemCount);
    setIsTransitioning(false);
    setTransitionToEnd(false);
  }, [isTransitioning, itemCount]);

  useEffect(() => {
    if (isTransitioning) transitionEndFired.current = false;
  }, [isTransitioning]);

  useEffect(() => {
    if (!enabled || itemCount <= 1) return;
    const id = setInterval(() => {
      setIsTransitioning(true);
      setTransitionToEnd(false);
    }, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, itemCount]);

  useEffect(() => {
    if (!isTransitioning) return;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setTransitionToEnd(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [isTransitioning]);

  return {
    index,
    nextIndex,
    isTransitioning,
    transitionToEnd,
    handleTransitionEnd,
    viewportHeight,
    transitionDurationSec: scrollDurationMs / 1000,
  };
}
